import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import Paragraph from "atoms/typography/paragraph";
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import ShareSpacePanelItem from "./share-space-panel-item";
import { useBookviewTabPanel } from "utilities/contexts/bookview-tab-panel/use-bookview-tab-panel";
import ShareSpacePanelEmailFooter from "./share-space-panel-email-footer";
import ShareSpacePanelAtom from "utilities/atoms/bookview/share-space-panel-atom";
import { ToastManager } from "utilities/toast/toast-manager";
import useBuildShareSpacePanel from "utilities/hooks/share-space-panel/use-build-share-space-panel";
import { CollectionUtils } from "utilities/collection-utils";
import Loader from "atoms/loaders/loader";
import { LoaderStyles } from "atoms/constants/loader-styles";
import { t } from "utilities/localization-utils";
import SortableDragAndDropList from "organisms/sortable-drap-and-drop/sortable-drag-and-drop-list";
import SortableDragAndDropItem from "organisms/sortable-drap-and-drop/sortable-drag-and-drop-item";
import ShareSpaceItemRecord from "models/view-models/share-spaces/share-space-item-record";
import ShareSpaceItem from "models/interfaces/share-spaces/share-space-item";
import ShareSpaceItemService from "utilities/services/share-space/share-space-items-service";
import { useAtom } from "jotai";
import Trans from "atoms/i18n/trans";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import useShareFormSubmit from "utilities/hooks/use-share-form-submit";
import ShareSpaceSubmitButton from "atoms/buttons/share-space-submit-button";

const BASE_CLASS_NAME = "c-share-space-panel";
const ShareSpaceErrorKeys = {
    General: "General",
};
const DEFAULT_MAX_SHARE_SPACE_ITEMS = 25;

const ShareSpacePanel: React.FC<PropsWithChildren> = (props) => {
    const { isShareSpacePanelOpen: isOpen } = useBookviewTabPanel();
    const {
        globalState: { systemSettings },
    } = useGlobalState();

    const [shareSpacePanel, setShareSpacePanel] = useAtom(ShareSpacePanelAtom);
    const { loading: isShareSpaceLoading } = useBuildShareSpacePanel();
    const [isLoading, setIsLoading] = useState(false);
    const [isSorting, setIsSorting] = useState(false);
    const { shareSpace } = shareSpacePanel;
    const { delete: deleteAllItemsApi } = ShareSpaceItemService.useDeleteAll();
    const { inMobileView: showMobileNav } = useBreakpointRanges();
    const ref = useRef<HTMLDivElement | null>(null);
    const [validationErrors, setValidationErrors] = useState("");

    const { handleFormSubmit } = useShareFormSubmit(
        setIsLoading,
        setValidationErrors
    );

    const title =
        shareSpacePanel.getShareItemsLength() > 1
            ? t("items-readyToShare__plural", {
                  count: shareSpacePanel.getShareItemsLength(),
              })
            : t("item-readyToShare__singular");

    const singleItemReadyToShare = shareSpacePanel.getShareItemsLength() === 1;

    const handleOnSortChange = async (
        sortedShareSpaceItems: ShareSpaceItem[]
    ) => {
        setIsSorting(true);
        try {
            const itemsAsRecords = sortedShareSpaceItems.map((item) =>
                new ShareSpaceItemRecord().with({ ...item })
            );

            // Find ones that changed
            const shareSpaceItemsToUpdate = itemsAsRecords.filter(
                (reorderedItem) =>
                    reorderedItem.displaySequence !==
                    shareSpace.shareSpaceItems?.find(
                        (item) => item.id === reorderedItem.id
                    )?.displaySequence
            );

            setShareSpacePanel((previousShareSpacePanel) => {
                return previousShareSpacePanel.with({
                    shareSpace: previousShareSpacePanel.shareSpace.with({
                        shareSpaceItems: itemsAsRecords,
                    }),
                });
            });

            const sanitizedItemsToUpdate = shareSpaceItemsToUpdate.map((item) =>
                item.prepareForUpdate()
            );

            const updateResult = await ShareSpaceItemService.bulkUpdate(
                sanitizedItemsToUpdate,
                { shareSpaceId: shareSpace.id! }
            );

            if (updateResult.result?.hasErrors()) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("updating"),
                        resource: t("share"),
                    })
                );
            }
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("updating"),
                    resource: t("share"),
                })
            );
        } finally {
            setIsSorting(false);
        }
    };

    const handleDeleteShareItem = async (shareSpaceItem: ShareSpaceItem) => {
        try {
            const deleteItemResponse = await ShareSpaceItemService.delete(
                shareSpacePanel.shareSpace.id!,
                {
                    shareSpaceId: shareSpace.id,
                    shareSpaceItemId: shareSpaceItem.id,
                }
            );
            if (deleteItemResponse.result?.errors?.length) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("removing"),
                        resource: t("section"),
                    })
                );
            }

            const filteredItems = shareSpace.shareSpaceItems!.filter(
                (item) => item.id !== shareSpaceItem.id
            );

            setShareSpacePanel(
                shareSpacePanel.with({
                    shareSpace: shareSpace.with({
                        shareSpaceItems: filteredItems,
                    }),
                })
            );
        } catch (e) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("removing"),
                    resource: t("section"),
                })
            );
        }
    };

    const handleClearAllShareItems = async () => {
        try {
            const deleteAllResponse = await deleteAllItemsApi(
                undefined as any,
                {
                    shareSpaceId: shareSpace.id!,
                }
            );

            if (deleteAllResponse.result?.hasErrors()) {
                throw new Error(ShareSpaceErrorKeys.General);
            }

            setShareSpacePanel(
                shareSpacePanel.with({
                    shareSpace: shareSpace.with({
                        shareSpaceItems: [],
                    }),
                })
            );
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("removing"),
                    resource: t("share"),
                })
            );
        }
    };

    useEffect(() => {
        if (ref.current && isOpen) ref.current.focus();
    }, [isOpen]);

    return (
        <div className={BASE_CLASS_NAME}>
            {(isLoading || isShareSpaceLoading) && !isSorting ? (
                <div className={`${BASE_CLASS_NAME}__loaderWrapper`}>
                    <Loader type={LoaderStyles.LinkGlyph} />
                </div>
            ) : (
                <>
                    <div className={`${BASE_CLASS_NAME}__body`}>
                        {CollectionUtils.isEmpty(
                            shareSpace?.shareSpaceItems
                        ) ? (
                            <div className={`${BASE_CLASS_NAME} no-items`}>
                                <div>
                                    <Icon
                                        type={Icons.ReferencePanel}
                                        size={IconSizes.XLarge}></Icon>
                                </div>
                                <Paragraph>
                                    {t("items-readyToShare__plural", {
                                        count: 0,
                                    })}
                                </Paragraph>
                                <Trans className={`${BASE_CLASS_NAME}--`}>
                                    {" "}
                                    <Paragraph>
                                        Use the <Icon type={Icons.Share} /> icon
                                        to add an item.
                                    </Paragraph>
                                </Trans>
                            </div>
                        ) : (
                            <>
                                <div className={`${BASE_CLASS_NAME}__header`}>
                                    <span>{title}</span>
                                </div>
                                <div className={`${BASE_CLASS_NAME}__items`}>
                                    {isShareSpaceLoading && !isSorting ? (
                                        <Loader type={LoaderStyles.LinkGlyph} />
                                    ) : (
                                        <SortableDragAndDropList
                                            items={shareSpace.shareSpaceItems}
                                            onSortChange={handleOnSortChange}>
                                            {shareSpace.shareSpaceItems.map(
                                                (item) => {
                                                    return (
                                                        <SortableDragAndDropItem
                                                            id={item.id!}
                                                            key={item.id!}>
                                                            {
                                                                <ShareSpacePanelItem
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    sharedItem={
                                                                        item
                                                                    }
                                                                    onDelete={
                                                                        handleDeleteShareItem
                                                                    }
                                                                />
                                                            }
                                                        </SortableDragAndDropItem>
                                                    );
                                                }
                                            )}
                                        </SortableDragAndDropList>
                                    )}
                                    <Button
                                        size={ButtonSizes.Small}
                                        style={ButtonStyles.TertiaryAlt}
                                        onClick={handleClearAllShareItems}>
                                        {"Clear All"}
                                    </Button>
                                </div>
                                {singleItemReadyToShare && (
                                    <div
                                        className={`${BASE_CLASS_NAME}__single-item-info`}>
                                        {t(
                                            "shareSpaceItem__singular-item-info",
                                            {
                                                max:
                                                    systemSettings
                                                        ?.shareSpaceConfig
                                                        ?.shareSpaceItemsMaximumPerShareSpace ??
                                                    DEFAULT_MAX_SHARE_SPACE_ITEMS,
                                            }
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${BASE_CLASS_NAME}__footer`}>
                        <ShareSpacePanelEmailFooter
                            handleFormSubmit={handleFormSubmit}
                            validationErrors={validationErrors}
                            loading={isLoading}
                        />
                    </div>
                    {showMobileNav && (
                        <div
                            className={`${BASE_CLASS_NAME}__mobile-share-footer`}>
                            {props.children}
                            <ShareSpaceSubmitButton
                                onClick={handleFormSubmit}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ShareSpacePanel;
